import { ApraoLogoType } from "@aprao/common/src/ApraoLogoType.js";
import type { JsonDate } from "@aprao/common/src/shapes/common.js";
import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import { css } from "@emotion/react";
import * as Sentry from "@sentry/browser";
import { parseJSON } from "date-fns";
import { type ReactNode, useEffect, useRef } from "react";
import { useEventCallback } from "usehooks-ts";

import { useI18n } from "../../common/ApraoI18n.js";
import { RichTextRenderer } from "../RichTextRenderer.js";

export const LocationMap = (props: { mapImageUrl: string | null }) => {
  const { msg } = useI18n();

  return (
    <p
      css={css`
        text-align: center;
        margin: 50px 0;
      `}
      data-testid="cp__location-map"
    >
      {props.mapImageUrl == null ?
        <>{msg("report.mapError")}</>
      : <img
          css={css`
            width: 100%;
            // prevent shift of layout while the image is loading
            // see buildMapImageUrl for the value's rationale
            aspect-ratio: 2 / 1;
          `}
          src={props.mapImageUrl}
          // Google sometimes changes the copyright under the map which
          // triggers false positive in visual test.
          data-visual-test="transparent"
        />
      }
    </p>
  );
};

export const CoverPage = (props: {
  reportTitle: string;
  appraisal: AppraisalDocumentFacade;
  updatedDate: JsonDate;
  mapImageUrl: string | null;
  logoUrl: string | null;
  slots: { locationMap: ReactNode };
  onReady: () => void;
}) => {
  const { logoUrl, onReady, reportTitle, appraisal, updatedDate, slots } =
    props;

  const handleReady = useEventCallback(onReady);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Wait for all the images to be loaded before declaring the cover page
    // ready. It also works if there are no images since Promise.all resolves
    // when an empty array is given.
    Promise.all(
      Array.from(rootRef.current?.querySelectorAll("img") ?? []).map(
        async (img) =>
          img.complete ?
            Promise.resolve()
          : new Promise<void>((resolve) =>
              img.addEventListener("load", () => resolve(), { once: true }),
            ),
      ),
    )
      .then(handleReady)
      .catch((error) => Sentry.captureException(error));
  }, [handleReady]);

  const { msg, effectiveLocale } = useI18n();

  return (
    <div
      css={css`
        padding-top: 150px;
        position: relative;
      `}
      ref={rootRef}
    >
      <div
        css={css`
          height: 150px;
          position: absolute;
          right: 0;
          top: 0;
          width: 150px;

          display: flex;
          align-items: center;
          justify-content: right;
        `}
        // By pure randomness during the test run the organisation id could
        // match an existing logo so we hide it to avoid false positive
        // regression.
        data-visual-test="transparent"
      >
        {logoUrl == null ?
          <ApraoLogoType />
        : <img
            css={css`
              max-width: 100%;
              max-height: 100%;
            `}
            src={logoUrl}
          />
        }
      </div>
      <h2
        css={css`
          font-size: calc(1.4 * var(--report-root-font-size));
          font-weight: normal;
          margin: 0 0 30px;
        `}
      >
        {msg("report.developmentAppraisal")}
      </h2>
      <h1
        css={css`
          font-size: calc(1.7 * var(--report-root-font-size));
          font-weight: normal;
          line-height: calc(1.7 * var(--report-root-font-size));
          margin: 0 0 30px;
        `}
      >
        {reportTitle}
      </h1>
      <h3
        css={css`
          font-size: calc(1.3 * var(--report-root-font-size));
          font-weight: normal;
          margin: 0 0 30px;
        `}
        // The created changes at every run, so we hide it to visual regression
        // test.
        data-visual-test="transparent"
      >
        {parseJSON(updatedDate).toLocaleDateString(effectiveLocale, {
          dateStyle: "long",
        })}
      </h3>
      {slots.locationMap}
      <h3
        css={css`
          font-size: calc(1.3 * var(--report-root-font-size));
          font-weight: normal;
          margin: 0 0 calc(2.5 * var(--report-root-font-size));
        `}
      >
        Summary
      </h3>
      <RichTextRenderer value={appraisal.summary} />
    </div>
  );
};
